import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import {
  addBookingExtraCharges,
  removeBookingExtraChargeItem,
  updateBookingExtraChargeItem,
} from '../../services/api';
import {
  formatBookingCurrency,
  getBookingCurrencySymbol,
} from '../../utils/bookingPaymentSummary';
import {
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
  getBookingDrawerFooterSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPaperSx,
  getBookingDrawerTheme,
} from '../../utils/bookingDrawerLayout';
import {
  aggregateExtraChargeRows,
  buildExtraChargesSummary,
  calculateExtraChargePreview,
  matchesPresetGstPercent,
  normalizeExtraChargeItem,
  parseGstPercentInput,
  resolveExtraChargeGstDefaults,
} from '../../utils/extraChargesGstUtils';

const REMOVE_CONFIRM_WORD = 'remove';

function newLocalExtraChargeId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function toApiExtraChargeItem(row, index = 0) {
  const normalized = normalizeExtraChargeItem(row, row?.reason || 'Extra Charges', index);
  return {
    id: normalized.id || newLocalExtraChargeId(),
    reason: normalized.reason,
    base_amount: Number(normalized.base || 0).toFixed(2),
    gst_percent: Number(normalized.gstPercent || 0),
    gst_amount: Number(normalized.gstAmount || 0).toFixed(2),
    total_amount: Number(normalized.total || 0).toFixed(2),
  };
}

function buildLocalExtraChargesResult(items = []) {
  const apiItems = (items || [])
    .map((item, index) => toApiExtraChargeItem(item, index))
    .filter((item) => Number(item.total_amount) > 0 || Number(item.base_amount) > 0);
  const rows = apiItems.map((item, index) => normalizeExtraChargeItem(item, item.reason, index));
  const totals = aggregateExtraChargeRows(rows);
  const note = rows
    .map((row) => row.reason)
    .filter(Boolean)
    .join('; ')
    .slice(0, 500);
  return {
    extra_charges_items: apiItems,
    extra_charges_amount: totals.total,
    extra_charges_base_amount: totals.base,
    extra_charges_gst_amount: totals.gstAmount,
    extra_charges_note: note,
  };
}

function RemoveExtraChargeConfirmDialog({
  open,
  row,
  currency,
  isDark,
  accent,
  drawerTheme,
  modalContainer,
  confirmText,
  onConfirmTextChange,
  onClose,
  onConfirm,
  removing,
}) {
  const confirmInputRef = useRef(null);
  const confirmValid = confirmText.trim().toLowerCase() === REMOVE_CONFIRM_WORD;
  const gstLabel = row?.gstAmount > 0
    ? `GST ${row.gstPercent}% · ${formatBookingCurrency(row.gstAmount, currency)}`
    : 'No GST';

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => {
      confirmInputRef.current?.focus();
    }, 120);
    return () => window.clearTimeout(timer);
  }, [open]);

  const resolveContainer = () => {
    if (typeof modalContainer === 'function') return modalContainer() || undefined;
    return modalContainer || undefined;
  };

  return (
    <Dialog
      open={open}
      onClose={() => !removing && onClose?.()}
      container={resolveContainer}
      disableScrollLock
      sx={{
        position: 'absolute',
        zIndex: 1600,
        '& .MuiBackdrop-root': {
          position: 'absolute',
        },
        '& .MuiDialog-container': {
          position: 'absolute',
          inset: 0,
        },
      }}
      BackdropProps={{
        sx: {
          position: 'absolute',
          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.68)' : 'rgba(0, 0, 0, 0.45)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: isDark ? drawerTheme.surface : '#fff',
          border: `1px solid ${isDark ? alpha('#f44336', 0.22) : alpha('#f44336', 0.14)}`,
          boxShadow: isDark
            ? '0 12px 28px rgba(0, 0, 0, 0.45)'
            : '0 10px 24px rgba(0, 0, 0, 0.14)',
          width: '100%',
          maxWidth: 388,
          m: 1.5,
          position: 'relative',
          zIndex: 1,
        },
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          background: isDark
            ? `linear-gradient(120deg, ${alpha('#f44336', 0.28)} 0%, ${alpha('#ff7043', 0.12)} 42%, ${alpha(accent, 0.08)} 100%)`
            : `linear-gradient(120deg, ${alpha('#f44336', 0.14)} 0%, ${alpha('#ff8a65', 0.08)} 50%, ${alpha(accent, 0.05)} 100%)`,
          borderBottom: `1px solid ${alpha('#f44336', isDark ? 0.22 : 0.12)}`,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '9px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: `linear-gradient(145deg, ${alpha('#f44336', 0.9)} 0%, #c62828 100%)`,
            boxShadow: `0 4px 12px ${alpha('#f44336', 0.35)}`,
          }}
        >
          <DeleteForeverIcon sx={{ color: '#fff', fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: drawerTheme.text, lineHeight: 1.15, fontSize: '0.84rem' }}
          >
            Remove extra charge
          </Typography>
          <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontSize: '0.65rem' }}>
            Permanent · logged in audit
          </Typography>
        </Box>
        <IconButton
          size="small"
          disabled={removing}
          onClick={onClose}
          sx={{
            width: 28,
            height: 28,
            color: drawerTheme.subtleText,
            backgroundColor: isDark ? alpha('#000', 0.22) : alpha('#fff', 0.65),
            border: `1px solid ${alpha('#fff', isDark ? 0.08 : 0.5)}`,
          }}
        >
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: 1.5, py: 1.25 }}>
        <Paper
          elevation={0}
          sx={{
            px: 1,
            py: 0.85,
            mb: 0.9,
            borderRadius: '9px',
            backgroundColor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
            border: `1px solid ${alpha(accent, isDark ? 0.14 : 0.1)}`,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.75}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: drawerTheme.text,
                  fontSize: '0.8rem',
                  lineHeight: 1.3,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {row?.reason || 'Extra charge'}
              </Typography>
              <Chip
                size="small"
                label={gstLabel}
                sx={{
                  mt: 0.5,
                  height: 20,
                  fontSize: '0.62rem',
                  fontWeight: 600,
                  maxWidth: '100%',
                  backgroundColor: row?.gstAmount > 0
                    ? (isDark ? alpha('#90caf9', 0.12) : alpha('#1565c0', 0.08))
                    : (isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04)),
                  color: row?.gstAmount > 0
                    ? (isDark ? '#90caf9' : '#1565c0')
                    : drawerTheme.subtleText,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '0.95rem',
                color: isDark ? '#ffcc80' : '#e65100',
                lineHeight: 1.1,
                flexShrink: 0,
              }}
            >
              {formatBookingCurrency(row?.total || 0, currency)}
            </Typography>
          </Stack>
        </Paper>

        <Stack direction="row" spacing={0.6} alignItems="flex-start" sx={{ mb: 0.9 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 15, color: '#ff9800', mt: 0.1 }} />
          <Typography variant="caption" sx={{ color: drawerTheme.subtleText, lineHeight: 1.35, fontSize: '0.68rem' }}>
            Booking total will update immediately. This cannot be undone.
          </Typography>
        </Stack>

        <Typography variant="caption" sx={{ color: drawerTheme.subtleText, mb: 0.5, display: 'block', fontSize: '0.68rem' }}>
          Type{' '}
          <Box
            component="span"
            sx={{
              px: 0.45,
              py: 0.05,
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.68rem',
              fontWeight: 700,
              color: confirmValid ? accent : drawerTheme.text,
              backgroundColor: isDark ? alpha(accent, 0.1) : alpha(accent, 0.08),
              border: `1px solid ${confirmValid ? alpha(accent, 0.45) : (isDark ? alpha('#fff', 0.1) : alpha('#000', 0.1))}`,
            }}
          >
            {REMOVE_CONFIRM_WORD}
          </Box>
          {' '}to confirm
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder={`Type ${REMOVE_CONFIRM_WORD}`}
          value={confirmText}
          onChange={(e) => onConfirmTextChange?.(e.target.value)}
          inputRef={confirmInputRef}
          error={confirmText.length > 0 && !confirmValid}
          disabled={removing}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon
                  sx={{ fontSize: 16, color: confirmValid ? accent : drawerTheme.subtleText }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '9px',
              fontSize: '0.86rem',
              minHeight: 40,
              backgroundColor: isDark ? alpha('#000', 0.18) : alpha('#000', 0.02),
              '& fieldset': { borderColor: isDark ? alpha('#fff', 0.12) : alpha('#000', 0.12) },
              '&.Mui-focused fieldset': {
                borderColor: confirmValid ? accent : (isDark ? alpha('#fff', 0.28) : alpha('#000', 0.28)),
              },
            },
            '& .MuiOutlinedInput-input': {
              py: 1,
            },
          }}
        />

        <Stack direction="row" spacing={0.85} sx={{ mt: 1.15 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={onClose}
            disabled={removing}
            sx={{
              flex: 1,
              py: 0.55,
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.78rem',
              color: drawerTheme.text,
              borderColor: isDark ? alpha('#fff', 0.14) : alpha('#000', 0.14),
            }}
          >
            Keep
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={removing || !confirmValid}
            onClick={onConfirm}
            startIcon={
              removing ? (
                <CircularProgress size={13} color="inherit" />
              ) : (
                <DeleteForeverIcon sx={{ fontSize: 15 }} />
              )
            }
            sx={{
              flex: 1,
              py: 0.55,
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '0.78rem',
              boxShadow: confirmValid && !removing ? '0 3px 10px rgba(244, 67, 54, 0.28)' : 'none',
            }}
          >
            {removing ? 'Removing…' : 'Remove'}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

const QUICK_REASONS = [
  'Extra mattress',
  'Extra bed',
  'Extra pillow / linen',
  'Extra guest',
  'Baby cot',
  'Late checkout',
  'Early check-in',
  'Room service',
  'Minibar',
  'Food & beverage',
  'Laundry',
  'Dry cleaning',
  'Parking',
  'Wi-Fi / internet',
  'Airport transfer',
  'Cab / transport',
  'Pet fee',
  'Smoking fine',
  'TV / appliance damage',
  'Room damage',
  'Lost key / card',
  'Missing items',
  'Spa / wellness',
  'Doctor / medicine',
  'Conference / meeting',
];

function parseReasonTokens(text) {
  return (text || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function joinReasonTokens(tokens) {
  return tokens.join(', ');
}

function appendUniqueReason(currentNote, reason) {
  const tokens = parseReasonTokens(currentNote);
  const key = reason.trim().toLowerCase();
  if (!key || tokens.some((token) => token.toLowerCase() === key)) {
    return joinReasonTokens(tokens);
  }
  return joinReasonTokens([...tokens, reason.trim()]);
}

function isReasonSelected(note, reason) {
  const key = reason.trim().toLowerCase();
  return parseReasonTokens(note).some((token) => token.toLowerCase() === key);
}

function normalizeReasonNote(text) {
  const seen = new Set();
  const unique = [];
  parseReasonTokens(text).forEach((token) => {
    const key = token.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(token);
    }
  });
  return joinReasonTokens(unique);
}

const roundCurrency = (value) => Math.round((Number(value) || 0) * 100) / 100;

function AmountField({ value, onChange, isDark, disabled, currency = 'INR' }) {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDraft(value > 0 ? String(roundCurrency(value)) : '');
    }
  }, [value, focused]);

  const handleBlur = () => {
    setFocused(false);
    const parsed = parseFloat(draft);
    const next = Number.isFinite(parsed) ? roundCurrency(Math.max(0, parsed)) : 0;
    onChange(next);
    setDraft(next > 0 ? String(next) : '');
  };

  return (
    <TextField
      fullWidth
      size="small"
      label="Charge amount (excl. GST)"
      disabled={disabled}
      value={focused ? draft : (value > 0 ? roundCurrency(value).toFixed(2) : '')}
      onFocus={() => {
        setFocused(true);
        setDraft(value > 0 ? String(roundCurrency(value)) : '');
      }}
      onChange={(e) => setDraft(e.target.value.replace(/[^\d.]/g, ''))}
      onBlur={handleBlur}
      placeholder="0.00"
      inputMode="decimal"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {getBookingCurrencySymbol(currency)}
          </InputAdornment>
        ),
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          fontSize: '0.9375rem',
          backgroundColor: isDark ? alpha('#fff', 0.04) : '#fff',
        },
      }}
    />
  );
}

function GstPercentField({ value, onChange, options, isDark, disabled, accent, drawerTheme }) {
  const [customDraft, setCustomDraft] = useState('');
  const [customFocused, setCustomFocused] = useState(false);

  const presetValue = matchesPresetGstPercent(value, options) ? roundCurrency(value) : null;

  useEffect(() => {
    if (!customFocused && !matchesPresetGstPercent(value, options) && value > 0) {
      setCustomDraft(String(roundCurrency(value)));
    }
  }, [value, customFocused, options]);

  const handleCustomBlur = () => {
    setCustomFocused(false);
    const parsed = parseGstPercentInput(customDraft);
    onChange(parsed);
    setCustomDraft(parsed > 0 && !matchesPresetGstPercent(parsed, options) ? String(roundCurrency(parsed)) : '');
  };

  return (
    <Stack spacing={1}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={presetValue}
        disabled={disabled}
        onChange={(_, next) => {
          if (next == null) return;
          onChange(next);
          setCustomDraft('');
          setCustomFocused(false);
        }}
        sx={{
          flexWrap: 'wrap',
          gap: 0.5,
          '& .MuiToggleButton-root': {
            px: 1.25,
            py: 0.35,
            fontSize: '0.75rem',
            fontWeight: 700,
            borderRadius: '8px !important',
            border: `1px solid ${alpha(accent, 0.25)} !important`,
            color: drawerTheme.text,
          },
          '& .Mui-selected': {
            backgroundColor: `${alpha(accent, isDark ? 0.24 : 0.16)} !important`,
            color: isDark ? accent : '#0f766e',
          },
        }}
      >
        {options.map((option) => (
          <ToggleButton key={option} value={option}>
            {option}%
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <TextField
        fullWidth
        size="small"
        label="Or enter custom %"
        placeholder="e.g. 28"
        disabled={disabled}
        value={
          customFocused
            ? customDraft
            : (matchesPresetGstPercent(value, options) ? customDraft : (value > 0 ? String(roundCurrency(value)) : customDraft))
        }
        onFocus={() => {
          setCustomFocused(true);
          if (!customDraft && value > 0 && !matchesPresetGstPercent(value, options)) {
            setCustomDraft(String(roundCurrency(value)));
          }
        }}
        onChange={(e) => setCustomDraft(e.target.value.replace(/[^\d.]/g, ''))}
        onBlur={handleCustomBlur}
        inputMode="decimal"
        InputProps={{
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '0.875rem',
            backgroundColor: isDark ? alpha('#fff', 0.04) : '#fff',
          },
        }}
        helperText={
          !matchesPresetGstPercent(value, options) && value > 0
            ? `Using custom rate: ${roundCurrency(value)}%`
            : 'Use when the rate is not in the chips above'
        }
      />
    </Stack>
  );
}

function SummaryRow({ label, value, valueColor, labelSx, valueSx }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
      <Typography variant="caption" sx={{ color: 'inherit', ...labelSx }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 600, color: valueColor, ...valueSx }}>
        {value}
      </Typography>
    </Stack>
  );
}

function ExtraChargeLineRow({
  row,
  currency,
  isDark,
  accent,
  drawerTheme,
  onEdit,
  onRemove,
  isEditing = false,
  disableActions = false,
  removing = false,
}) {
  const gstLabel = row.gstPercent > 0 ? `GST ${row.gstPercent}%` : 'No GST';
  return (
    <Box
      sx={{
        py: 0.75,
        px: 0.75,
        mx: -0.75,
        borderRadius: 1.5,
        borderBottom: `1px solid ${alpha(accent, isDark ? 0.12 : 0.1)}`,
        backgroundColor: isEditing ? alpha(accent, isDark ? 0.14 : 0.1) : 'transparent',
        '&:last-child': { borderBottom: 'none', pb: 0 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 0.35 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 700,
            color: row.isPending ? (isDark ? accent : '#0f766e') : drawerTheme.text,
            flex: 1,
            minWidth: 0,
          }}
        >
          {row.reason}
          {row.isPending ? ' (adding)' : ''}
          {isEditing ? ' (editing)' : ''}
        </Typography>
        {!row.isPending && onEdit && onRemove ? (
          <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
            <IconButton
              size="small"
              aria-label={`Edit ${row.reason}`}
              disabled={disableActions}
              onClick={() => onEdit(row)}
              sx={{
                width: 26,
                height: 26,
                color: isDark ? accent : '#0f766e',
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <IconButton
              size="small"
              aria-label={`Remove ${row.reason}`}
              disabled={disableActions}
              onClick={() => onRemove(row)}
              sx={{
                width: 26,
                height: 26,
                color: '#ef5350',
              }}
            >
              {removing ? (
                <CircularProgress size={14} sx={{ color: '#ef5350' }} />
              ) : (
                <DeleteOutlineIcon sx={{ fontSize: 15 }} />
              )}
            </IconButton>
          </Stack>
        ) : null}
      </Stack>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography variant="caption" sx={{ color: drawerTheme.subtleText }}>
          Base
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600, color: drawerTheme.text }}>
          {formatBookingCurrency(row.base, currency)}
        </Typography>
      </Stack>
      {row.gstAmount > 0 ? (
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Typography variant="caption" sx={{ color: drawerTheme.subtleText }}>
            {gstLabel}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: isDark ? '#90caf9' : '#1565c0' }}>
            {formatBookingCurrency(row.gstAmount, currency)}
          </Typography>
        </Stack>
      ) : null}
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography variant="caption" sx={{ color: drawerTheme.subtleText }}>
          Line total
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#ffcc80' : '#e65100' }}>
          {formatBookingCurrency(row.total, currency)}
        </Typography>
      </Stack>
    </Box>
  );
}

function ExtraChargesSummaryPanel({
  summary,
  currency,
  isDark,
  accent,
  drawerTheme,
  editingItemIndex,
  onEditItem,
  onRemoveItem,
  disableActions = false,
  removingItemIndex = null,
}) {
  if (!summary?.showSummary) return null;

  const { existingRows, pendingRow, existingTotals, projectedTotals, hasExisting, hasPending } = summary;

  return (
    <Box
      sx={{
        mt: 1.75,
        p: 1.25,
        borderRadius: 2,
        border: `1px dashed ${alpha(accent, 0.35)}`,
        backgroundColor: isDark ? alpha(accent, 0.08) : alpha(accent, 0.06),
      }}
    >
      <Typography
        variant="caption"
        sx={{ display: 'block', fontWeight: 700, color: drawerTheme.text, mb: 1 }}
      >
        Extra charges summary
      </Typography>

      {hasExisting ? (
        <Box sx={{ mb: hasPending ? 1.25 : 0 }}>
          <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontWeight: 600, display: 'block', mb: 0.5 }}>
            Already on booking
          </Typography>
          {existingRows.map((row, index) => (
            <ExtraChargeLineRow
              key={`${row.id || row.reason}-${index}`}
              row={row}
              currency={currency}
              isDark={isDark}
              accent={accent}
              drawerTheme={drawerTheme}
              isEditing={editingItemIndex != null && row.itemIndex === editingItemIndex}
              onEdit={onEditItem}
              onRemove={onRemoveItem}
              disableActions={disableActions}
              removing={removingItemIndex != null && row.itemIndex === removingItemIndex}
            />
          ))}
          {existingRows.length > 1 ? (
            <Box sx={{ pt: 0.75 }}>
              <SummaryRow
                label="Existing subtotal"
                value={formatBookingCurrency(existingTotals.total, currency)}
                valueColor={drawerTheme.text}
              />
            </Box>
          ) : null}
        </Box>
      ) : null}

      {hasPending ? (
        <Box sx={{ mb: 1.25 }}>
          <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontWeight: 600, display: 'block', mb: 0.5 }}>
            This add
          </Typography>
          <ExtraChargeLineRow
            row={pendingRow}
            currency={currency}
            isDark={isDark}
            accent={accent}
            drawerTheme={drawerTheme}
          />
        </Box>
      ) : null}

      <Box
        sx={{
          pt: 1,
          mt: 0.5,
          borderTop: `1px solid ${alpha(accent, isDark ? 0.2 : 0.16)}`,
        }}
      >
        <Stack spacing={0.5}>
          <SummaryRow
            label="Subtotal (excl. GST)"
            value={formatBookingCurrency(projectedTotals.base, currency)}
            valueColor={drawerTheme.text}
          />
          {projectedTotals.gstAmount > 0 ? (
            <SummaryRow
              label="Total GST"
              value={formatBookingCurrency(projectedTotals.gstAmount, currency)}
              valueColor={isDark ? '#90caf9' : '#1565c0'}
            />
          ) : null}
          <Box
            sx={{
              mt: 0.5,
              px: 1,
              py: 0.75,
              borderRadius: 1.5,
              backgroundColor: isDark ? alpha(accent, 0.16) : alpha(accent, 0.12),
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ fontWeight: 700, color: drawerTheme.text }}>
                {hasPending
                  ? 'All extra charges after add'
                  : editingItemIndex != null
                    ? 'All extra charges after update'
                    : 'All extra charges'}
              </Typography>
              <Typography sx={{ fontWeight: 800, color: isDark ? '#ffcc80' : '#e65100', fontSize: '0.95rem' }}>
                {formatBookingCurrency(projectedTotals.total, currency)}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

function ExtraChargesDrawerHeader({ onClose, submitting, isDark, accent, drawerTheme }) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        px: 2,
        py: 1.75,
        background: `linear-gradient(135deg, ${alpha(accent, isDark ? 0.28 : 0.18)} 0%, ${alpha('#fff', isDark ? 0.04 : 0.98)} 100%)`,
        borderBottom: `1px solid ${alpha(accent, 0.2)}`,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${accent} 0%, #3ab896 100%)`,
            color: '#000',
            boxShadow: `0 6px 18px ${alpha(accent, 0.35)}`,
          }}
        >
          <HandymanOutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: drawerTheme.text, fontSize: '1rem' }}>
            Extra Charges
          </Typography>
          <Typography variant="caption" sx={{ color: drawerTheme.subtleText }}>
            Add incidental or service fees with optional GST
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={submitting}
          aria-label="Close"
          sx={{ color: drawerTheme.subtleText }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

export default function BookingExtraChargesOverlay({
  open,
  onClose,
  bookingId,
  /** When true (or bookingId missing), manage charges in memory and call onSaved — for review / create flows. */
  localMode = false,
  currency = 'INR',
  existingAmount = 0,
  existingBaseAmount = 0,
  existingGstAmount = 0,
  existingNote = '',
  existingItems = [],
  roomGstPercent = 0,
  onSaved,
  isDark = false,
  accent = BOOKING_DRAWER_ACCENT,
  drawerContainerRef,
  /** Override paper width (e.g. QUICK_BOOKING_DRAWER_WIDTH_SX). Defaults to standard booking drawer. */
  drawerWidthSx = BOOKING_DRAWER_WIDTH_SX,
}) {
  const isLocalMode = Boolean(localMode) || !bookingId;
  const drawerTheme = getBookingDrawerTheme(isDark);
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState(0);
  const [lastReason, setLastReason] = useState('');
  const [applyGst, setApplyGst] = useState(true);
  const [gstPercent, setGstPercent] = useState(0);
  const [gstTouched, setGstTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [removingItemIndex, setRemovingItemIndex] = useState(null);
  const [removeDialog, setRemoveDialog] = useState({ open: false, row: null });
  const [removeConfirmText, setRemoveConfirmText] = useState('');
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [error, setError] = useState('');
  const [managedItems, setManagedItems] = useState([]);
  const [managedAmount, setManagedAmount] = useState(0);
  const [managedBaseAmount, setManagedBaseAmount] = useState(0);
  const [managedGstAmount, setManagedGstAmount] = useState(0);
  const [managedNote, setManagedNote] = useState('');
  const drawerRootRef = useRef(null);
  const nestedContainer = drawerContainerRef?.current || null;
  const modalContainer = useCallback(
    () => drawerRootRef.current || drawerContainerRef?.current || undefined,
    [drawerContainerRef],
  );

  const resetChargeForm = () => {
    setNote('');
    setAmount(0);
    setLastReason('');
    setApplyGst(true);
    setGstPercent(0);
    setGstTouched(false);
    setEditingItemIndex(null);
  };

  const applyExtraChargesResult = (result) => {
    setManagedItems(Array.isArray(result?.extra_charges_items) ? result.extra_charges_items : []);
    setManagedAmount(Number(result?.extra_charges_amount) || 0);
    setManagedBaseAmount(Number(result?.extra_charges_base_amount) || 0);
    setManagedGstAmount(Number(result?.extra_charges_gst_amount) || 0);
    setManagedNote(result?.extra_charges_note || '');
    onSaved?.(result);
  };

  const gstDefaults = useMemo(
    () => resolveExtraChargeGstDefaults({ note, lastReason, roomGstPercent }),
    [note, lastReason, roomGstPercent]
  );

  useEffect(() => {
    if (!open) {
      resetChargeForm();
      setError('');
      setSubmitting(false);
      setRemovingItemIndex(null);
      setRemoveDialog({ open: false, row: null });
      setRemoveConfirmText('');
      setManagedItems([]);
      setManagedAmount(0);
      setManagedBaseAmount(0);
      setManagedGstAmount(0);
      setManagedNote('');
    } else {
      const seededSummary = buildExtraChargesSummary({
        existingItems: Array.isArray(existingItems) ? existingItems : [],
        existingAmount,
        existingBaseAmount,
        existingGstAmount,
        existingNote,
      });
      const seededItems = seededSummary.existingRows.map((row, index) => toApiExtraChargeItem(row, index));
      setManagedItems(seededItems);
      setManagedAmount(Number(existingAmount) || seededSummary.existingTotals.total || 0);
      setManagedBaseAmount(Number(existingBaseAmount) || seededSummary.existingTotals.base || 0);
      setManagedGstAmount(Number(existingGstAmount) || seededSummary.existingTotals.gstAmount || 0);
      setManagedNote(existingNote || seededSummary.existingRows.map((row) => row.reason).filter(Boolean).join('; ') || '');
      resetChargeForm();
      setError('');
    }
  }, [open, existingItems, existingAmount, existingBaseAmount, existingGstAmount, existingNote]);

  useEffect(() => {
    if (!open || gstTouched) return;
    setApplyGst(gstDefaults.applyGst);
    setGstPercent(gstDefaults.gstPercent);
  }, [open, gstDefaults, gstTouched]);

  const chargesSummary = useMemo(
    () =>
      buildExtraChargesSummary({
        existingItems: managedItems,
        existingAmount: managedAmount,
        existingBaseAmount: managedBaseAmount,
        existingGstAmount: managedGstAmount,
        existingNote: managedNote,
        pendingReason: editingItemIndex == null ? (normalizeReasonNote(note) || 'New charge') : null,
        pendingPreview:
          editingItemIndex == null && amount > 0
            ? calculateExtraChargePreview(amount, gstPercent, applyGst)
            : null,
      }),
    [
      managedItems,
      managedAmount,
      managedBaseAmount,
      managedGstAmount,
      managedNote,
      note,
      amount,
      applyGst,
      gstPercent,
      editingItemIndex,
    ]
  );

  const handleReasonClick = (reason) => {
    setNote((prev) => appendUniqueReason(prev, reason));
    setLastReason(reason);
    setGstTouched(false);
  };

  const handleClose = () => {
    if (submitting || removingItemIndex != null || removeDialog.open) return;
    onClose?.();
  };

  const handleEditItem = (row) => {
    if (submitting || removingItemIndex != null) return;
    setEditingItemIndex(row.itemIndex);
    setNote(row.reason || '');
    setAmount(row.base || 0);
    setLastReason(parseReasonTokens(row.reason).slice(-1)[0] || '');
    const hasGst = Number(row.gstPercent) > 0;
    setApplyGst(hasGst);
    setGstPercent(hasGst ? roundCurrency(row.gstPercent) : 0);
    setGstTouched(true);
    setError('');
  };

  const handleClearToAdd = () => {
    resetChargeForm();
    setError('');
  };

  const handleCloseRemoveDialog = () => {
    if (removingItemIndex != null) return;
    setRemoveDialog({ open: false, row: null });
    setRemoveConfirmText('');
  };

  const handleRemoveItem = (row) => {
    if ((!isLocalMode && !bookingId) || submitting || removingItemIndex != null) return;
    if (row.itemIndex == null || row.itemIndex < 0) return;
    setRemoveConfirmText('');
    setRemoveDialog({ open: true, row });
    setError('');
  };

  const handleConfirmRemove = async () => {
    const row = removeDialog.row;
    if (!row || removingItemIndex != null) return;
    if (removeConfirmText.trim().toLowerCase() !== REMOVE_CONFIRM_WORD) return;

    setRemovingItemIndex(row.itemIndex);
    setError('');
    try {
      if (isLocalMode) {
        const nextItems = managedItems.filter((_, index) => index !== row.itemIndex);
        applyExtraChargesResult(buildLocalExtraChargesResult(nextItems));
        if (editingItemIndex === row.itemIndex) {
          resetChargeForm();
        } else if (editingItemIndex != null && editingItemIndex > row.itemIndex) {
          setEditingItemIndex(editingItemIndex - 1);
        }
      } else {
        if (!bookingId) return;
        const result = await removeBookingExtraChargeItem(bookingId, row.itemIndex);
        applyExtraChargesResult(result);
        if (editingItemIndex === row.itemIndex) {
          resetChargeForm();
        } else if (editingItemIndex != null && editingItemIndex > row.itemIndex) {
          setEditingItemIndex(editingItemIndex - 1);
        }
      }
      setRemoveDialog({ open: false, row: null });
      setRemoveConfirmText('');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to remove extra charge.');
    } finally {
      setRemovingItemIndex(null);
    }
  };

  const handleSave = async () => {
    if (!isLocalMode && !bookingId) return;
    if (!amount || amount <= 0) {
      setError('Enter a valid charge amount.');
      return;
    }
    if (!note.trim()) {
      setError('Add a short description for this charge.');
      return;
    }

    const normalizedNote = normalizeReasonNote(note);
    const effectiveGstPercent = applyGst ? roundCurrency(gstPercent) : 0;
    const payload = {
      amount: roundCurrency(amount),
      note: normalizedNote,
      gstPercent: effectiveGstPercent,
    };
    setSubmitting(true);
    setError('');
    try {
      if (isLocalMode) {
        const preview = calculateExtraChargePreview(payload.amount, payload.gstPercent, applyGst);
        const line = toApiExtraChargeItem(
          {
            id: editingItemIndex != null ? managedItems[editingItemIndex]?.id : newLocalExtraChargeId(),
            reason: normalizedNote,
            base_amount: preview.base,
            gst_percent: preview.gstPercent,
            gst_amount: preview.gstAmount,
            total_amount: preview.total,
          },
          editingItemIndex == null ? managedItems.length : editingItemIndex,
        );
        const nextItems =
          editingItemIndex == null
            ? [...managedItems, line]
            : managedItems.map((item, index) => (index === editingItemIndex ? line : item));
        applyExtraChargesResult(buildLocalExtraChargesResult(nextItems));
        if (editingItemIndex == null) {
          onClose?.();
        } else {
          resetChargeForm();
        }
      } else {
        const result = editingItemIndex == null
          ? await addBookingExtraCharges(bookingId, payload)
          : await updateBookingExtraChargeItem(bookingId, editingItemIndex, payload);
        applyExtraChargesResult(result);
        if (editingItemIndex == null) {
          onClose?.();
        } else {
          resetChargeForm();
        }
      }
    } catch (err) {
      setError(
        err.response?.data?.error
          || err.message
          || (editingItemIndex == null ? 'Failed to add extra charges.' : 'Failed to update extra charge.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const gstOptions = [...new Set(gstDefaults.options || [0, 5, 12, 18])].sort((a, b) => a - b);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      disableScrollLock={Boolean(nestedContainer)}
      SlideProps={{ direction: 'left' }}
      transitionDuration={{ enter: 350, exit: 250 }}
      ModalProps={
        nestedContainer
          ? {
              container: nestedContainer,
              style: { position: 'absolute' },
              disablePortal: true,
            }
          : undefined
      }
      sx={
        nestedContainer
          ? {
              position: 'absolute',
              zIndex: 28,
              '& .MuiBackdrop-root': { position: 'absolute' },
              '& .MuiDrawer-paper': { position: 'absolute' },
            }
          : undefined
      }
      PaperProps={{
        sx: {
          ...getBookingDrawerPaperSx(isDark),
          width: drawerWidthSx || BOOKING_DRAWER_WIDTH_SX,
          maxWidth: '100vw',
        },
      }}
    >
      <Box ref={drawerRootRef} sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <ExtraChargesDrawerHeader
          onClose={handleClose}
          submitting={submitting || removingItemIndex != null || removeDialog.open}
          isDark={isDark}
          accent={accent}
          drawerTheme={drawerTheme}
        />

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontWeight: 600, mb: 0.75, display: 'block' }}>
            Quick reasons
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mb: 1.75 }}>
            {QUICK_REASONS.map((reason) => {
              const selected = isReasonSelected(note, reason);
              return (
                <Chip
                  key={reason}
                  size="small"
                  label={reason}
                  clickable
                  disabled={submitting || removingItemIndex != null}
                  onClick={() => handleReasonClick(reason)}
                  sx={{
                    height: 26,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    backgroundColor: selected
                      ? isDark ? alpha(accent, 0.28) : alpha(accent, 0.2)
                      : isDark ? alpha(accent, 0.12) : alpha(accent, 0.1),
                    color: isDark ? accent : '#0f766e',
                    border: selected ? `1px solid ${alpha(accent, 0.55)}` : '1px solid transparent',
                    '&:hover': {
                      backgroundColor: isDark ? alpha(accent, 0.22) : alpha(accent, 0.16),
                    },
                  }}
                />
              );
            })}
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.75 }}>
            <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontWeight: 600 }}>
              {editingItemIndex == null ? 'Add new charge' : 'Edit selected charge'}
            </Typography>
            {editingItemIndex != null ? (
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearToAdd}
                disabled={submitting || removingItemIndex != null || removeDialog.open}
                startIcon={<ClearAllIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  py: 0.35,
                  px: 1,
                  borderRadius: 1.5,
                  color: isDark ? accent : '#0f766e',
                  borderColor: alpha(accent, isDark ? 0.45 : 0.35),
                  backgroundColor: isDark ? alpha(accent, 0.08) : alpha(accent, 0.05),
                  '&:hover': {
                    borderColor: accent,
                    backgroundColor: isDark ? alpha(accent, 0.14) : alpha(accent, 0.1),
                  },
                }}
              >
                Clear
              </Button>
            ) : null}
          </Stack>

          {editingItemIndex != null ? (
            <Box
              sx={{
                mb: 1.25,
                px: 1,
                py: 0.75,
                borderRadius: 1.5,
                border: `1px solid ${alpha(accent, isDark ? 0.28 : 0.22)}`,
                backgroundColor: isDark ? alpha(accent, 0.1) : alpha(accent, 0.06),
              }}
            >
              <Typography variant="caption" sx={{ color: drawerTheme.subtleText, lineHeight: 1.4 }}>
                You are updating an existing line. Tap <strong>Clear</strong> to reset the form and add a new charge instead.
              </Typography>
            </Box>
          ) : null}
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              placeholder="e.g. Extra mattress for 2nd guest"
              value={note}
              disabled={submitting || removingItemIndex != null}
              onChange={(e) => {
                setNote(e.target.value.slice(0, 120));
                setGstTouched(false);
              }}
              onBlur={() => setNote((prev) => normalizeReasonNote(prev))}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? alpha('#fff', 0.04) : '#fff',
                },
              }}
            />
            <AmountField
              value={amount}
              onChange={setAmount}
              isDark={isDark}
              disabled={submitting}
              currency={currency}
            />

            <Box
              sx={{
                p: 1.25,
                borderRadius: 2,
                border: `1px solid ${alpha(accent, 0.22)}`,
                backgroundColor: isDark ? alpha('#fff', 0.03) : alpha(accent, 0.04),
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: drawerTheme.text }}>
                  GST on this charge
                </Typography>
                <FormControlLabel
                  control={(
                    <Switch
                      size="small"
                      checked={applyGst}
                      disabled={submitting || removingItemIndex != null}
                      onChange={(e) => {
                        setGstTouched(true);
                        setApplyGst(e.target.checked);
                        if (!e.target.checked) setGstPercent(0);
                        else setGstPercent(gstDefaults.gstPercent || 5);
                      }}
                    />
                  )}
                  label={applyGst ? 'Apply' : 'No GST'}
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.75rem',
                      color: drawerTheme.subtleText,
                    },
                  }}
                />
              </Stack>

              <Typography variant="caption" sx={{ color: drawerTheme.subtleText, display: 'block', mb: 1 }}>
                {gstDefaults.helperText}
              </Typography>

              {applyGst ? (
                <GstPercentField
                  value={gstPercent}
                  options={gstOptions}
                  isDark={isDark}
                  disabled={submitting || removingItemIndex != null}
                  accent={accent}
                  drawerTheme={drawerTheme}
                  onChange={(next) => {
                    setGstTouched(true);
                    setGstPercent(next);
                  }}
                />
              ) : null}
            </Box>
          </Stack>

          <ExtraChargesSummaryPanel
            summary={chargesSummary}
            currency={currency}
            isDark={isDark}
            accent={accent}
            drawerTheme={drawerTheme}
            editingItemIndex={editingItemIndex}
            onEditItem={handleEditItem}
            onRemoveItem={handleRemoveItem}
            disableActions={submitting || removingItemIndex != null || removeDialog.open}
            removingItemIndex={removingItemIndex}
          />

          {error ? (
            <Typography variant="caption" sx={{ color: '#ef5350', display: 'block', mt: 1.25 }}>
              {error}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ ...getBookingDrawerFooterSx(isDark), p: 1.5, flexShrink: 0 }}>
          <Stack direction="row" spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClose}
              disabled={submitting || removingItemIndex != null || removeDialog.open}
              sx={getBookingDrawerOutlinedButtonSx(isDark)}
            >
              Close
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSave}
              disabled={submitting || removingItemIndex != null || removeDialog.open || amount <= 0 || !note.trim()}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : editingItemIndex == null ? (
                  <AddCircleOutlineIcon />
                ) : (
                  <EditOutlinedIcon />
                )
              }
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                background: `linear-gradient(135deg, ${accent} 0%, #3ab896 100%)`,
                color: '#000',
                boxShadow: `0 6px 16px ${alpha(accent, 0.35)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${accent} 0%, #2fa888 100%)`,
                },
                '&:disabled': {
                  background: isDark ? alpha('#fff', 0.08) : '#e0e0e0',
                  color: isDark ? alpha(drawerTheme.subtleText, 0.55) : '#757575',
                  boxShadow: 'none',
                },
              }}
            >
              {submitting
                ? 'Saving…'
                : editingItemIndex == null
                  ? 'Add Charge'
                  : 'Update Charge'}
            </Button>
          </Stack>
        </Box>

        <RemoveExtraChargeConfirmDialog
          open={removeDialog.open}
          row={removeDialog.row}
          currency={currency}
          isDark={isDark}
          accent={accent}
          drawerTheme={drawerTheme}
          modalContainer={modalContainer}
          confirmText={removeConfirmText}
          onConfirmTextChange={setRemoveConfirmText}
          onClose={handleCloseRemoveDialog}
          onConfirm={handleConfirmRemove}
          removing={removingItemIndex != null}
        />
      </Box>
    </Drawer>
  );
}
